import type { SimpleColumn } from "./types";

/**
 * Pure column-ORDER arithmetic, extracted from `useColumnStorage` so it can be
 * exercised without mounting a React tree.
 *
 * The stored order is per-user server state (`app_users.user_prefs`), written by
 * an older build and read by a newer one — treat it as untrusted: it can name
 * columns that no longer exist, miss columns that now do, and (via a racing
 * double-write) repeat one.
 */

/** Default display order: table columns only, ascending by `priority`. */
export function getDefaultColumnOrder(columns: SimpleColumn[]): string[] {
  return columns
    .filter((col) => !col.formOnly)
    .sort((a, b) => a.priority - b.priority)
    .map((col) => col.id);
}

/**
 * Merge a stored column order with the default order, back-filling any columns
 * that exist in the defaults but not in the stored order (new columns land at
 * their default position, or the end as a fallback).
 *
 * Ids the defaults no longer contain are dropped, so a saved order that names a
 * removed column degrades to "that column is gone" instead of rendering a blank
 * header. Repeated ids collapse to their first occurrence — a duplicate would
 * otherwise render the same column twice under one React key.
 */
export function mergeColumnOrder(
  defaultColumns: SimpleColumn[],
  storedOrder: string[] | undefined
): string[] {
  const defaultColumnOrder = getDefaultColumnOrder(defaultColumns);
  if (!storedOrder || storedOrder.length === 0) return defaultColumnOrder;

  const mergedColumnOrder = [...storedOrder];
  defaultColumnOrder.forEach((colId) => {
    if (!mergedColumnOrder.includes(colId)) {
      const defaultIndex = defaultColumnOrder.indexOf(colId);
      const insertAfter = defaultIndex > 0 ? defaultColumnOrder[defaultIndex - 1] : null;
      if (insertAfter && mergedColumnOrder.includes(insertAfter)) {
        mergedColumnOrder.splice(mergedColumnOrder.indexOf(insertAfter) + 1, 0, colId);
      } else {
        mergedColumnOrder.push(colId);
      }
    }
  });

  // Drop any stored ids that no longer exist in the defaults, and any repeat.
  const knownIds = new Set(defaultColumnOrder);
  const emitted = new Set<string>();
  return mergedColumnOrder.filter((id) => {
    if (!knownIds.has(id) || emitted.has(id)) return false;
    emitted.add(id);
    return true;
  });
}
